import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PropertyCard } from "../components/PropertyCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MagnifyingGlass, ArrowRight, House, Buildings, Storefront, MapTrifold } from "@phosphor-icons/react";

const HERO_BG = "https://images.unsplash.com/photo-1661434241244-63c4f42b8d87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx3aGl0ZXwxNzg0MzIwNjA4fDA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const [properties, setProperties] = useState([]);
  const [q, setQ] = useState("");
  const [op, setOp] = useState("todas");
  const [type, setType] = useState("todos");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/properties", { params: { limit: 8 } })
      .then(({ data }) => setProperties(data))
      .catch(() => setProperties([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (op !== "todas") params.set("operation", op);
    if (type !== "todos") params.set("property_type", type);
    navigate(`/propiedades?${params.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden grain">
        <img src={HERO_BG} alt="Casa moderna" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-white/80 font-semibold" data-testid="hero-eyebrow">
              Bienes raíces en México
            </p>
            <h1 className="mt-4 font-heading text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight" data-testid="hero-title">
              Encuentra el hogar<br />
              que ya te está esperando.
            </h1>
            <p className="mt-6 text-white/85 text-lg max-w-xl leading-relaxed">
              Explora casas, departamentos y terrenos publicados por agentes verificados en todo el país.
            </p>
          </div>

          {/* Search bar */}
          <form
            onSubmit={handleSearch}
            className="mt-12 bg-black/60 backdrop-blur-xl border border-[#C8A24A]/30 rounded-2xl p-3 flex flex-col md:flex-row gap-3 shadow-2xl max-w-4xl"
            data-testid="hero-search-form"
          >
            <div className="flex-1 flex items-center gap-2 px-4 bg-[#1A1A1A] rounded-xl border border-[#C8A24A]/20">
              <MagnifyingGlass size={20} className="text-[#C8A24A]/50" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ciudad, colonia o palabra clave"
                className="border-0 focus-visible:ring-0 h-12 px-0"
                data-testid="hero-search-input"
              />
            </div>
            <Select value={op} onValueChange={setOp}>
              <SelectTrigger className="md:w-40 h-14 bg-[#1A1A1A] border-[#C8A24A]/20" data-testid="hero-operation-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="venta">Comprar</SelectItem>
                <SelectItem value="renta">Rentar</SelectItem>
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="md:w-44 h-14 bg-[#1A1A1A] border-[#C8A24A]/20" data-testid="hero-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Cualquier tipo</SelectItem>
                <SelectItem value="casa">Casa</SelectItem>
                <SelectItem value="departamento">Departamento</SelectItem>
                <SelectItem value="terreno">Terreno</SelectItem>
                <SelectItem value="oficina">Oficina</SelectItem>
                <SelectItem value="local">Local</SelectItem>
                <SelectItem value="bodega">Bodega</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" size="lg" className="h-14 px-8 bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] rounded-xl" data-testid="hero-search-btn">
              Buscar <ArrowRight size={18} weight="bold" className="ml-2" />
            </Button>
          </form>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Explora por categoría</p>
            <h2 className="font-heading text-3xl lg:text-4xl mt-2 tracking-tight">Empieza tu búsqueda</h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: House, label: "Casas", type: "casa" },
            { icon: Buildings, label: "Departamentos", type: "departamento" },
            { icon: MapTrifold, label: "Terrenos", type: "terreno" },
            { icon: Storefront, label: "Locales", type: "local" },
          ].map(({ icon: Icon, label, type: t }) => (
            <Link
              key={t}
              to={`/propiedades?property_type=${t}`}
              className="group p-8 bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 hover:border-[#C8A24A] hover:shadow-lg transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1"
              data-testid={`category-${t}`}
            >
              <div className="w-14 h-14 rounded-xl bg-[#C8A24A]/10 flex items-center justify-center mb-4 group-hover:bg-[#C8A24A] transition-colors">
                <Icon size={28} weight="duotone" className="text-[#C8A24A] group-hover:text-white transition-colors" />
              </div>
              <p className="font-heading text-lg">{label}</p>
              <p className="text-sm text-[#C8A24A]/70 mt-1 flex items-center gap-1 group-hover:text-[#C8A24A]">
                Ver todos <ArrowRight size={14} />
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Properties */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Recién publicadas</p>
            <h2 className="font-heading text-3xl lg:text-4xl mt-2 tracking-tight">Propiedades destacadas</h2>
          </div>
          <Link to="/propiedades" className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#C8A24A] hover:gap-3 transition-all" data-testid="see-all-btn">
            Ver todas <ArrowRight size={16} />
          </Link>
        </div>

        {properties.length === 0 ? (
          <div className="bg-[#1A1A1A] rounded-2xl border border-dashed border-[#C8A24A]/20 p-16 text-center">
            <p className="font-heading text-2xl mb-2">Aún no hay propiedades publicadas</p>
            <p className="text-sm text-[#C8A24A]/70 mb-6">Sé el primer agente en publicar en ROCAFUERTE GO.</p>
            <Link to="/registro">
              <Button className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]" data-testid="empty-register-btn">
                Registrarme como agente
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {properties.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </section>

      {/* CTA Agents */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative bg-[#111111] text-white rounded-3xl overflow-hidden p-12 md:p-16">
          <div className="max-w-2xl relative z-10">
            <p className="text-xs uppercase tracking-[0.3em] text-white/70 font-semibold">Para agentes inmobiliarios</p>
            <h2 className="font-heading text-3xl lg:text-5xl mt-4 tracking-tight">Publica tus propiedades y llega a más clientes.</h2>
            <p className="mt-4 text-white/80 leading-relaxed">
              Crea una cuenta gratis, sube fotos, describe cada detalle y recibe consultas de clientes interesados directo en tu panel.
            </p>
            <Link to="/registro">
              <Button size="lg" className="mt-8 bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] rounded-xl h-14 px-8" data-testid="cta-agent-register-btn">
                Publicar mi propiedad <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
          <div className="absolute -right-16 -bottom-16 w-96 h-96 rounded-full bg-[#C8A24A]/20 blur-3xl" />
        </div>
      </section>
    </div>
  );
}
